import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Package, Plus, X, ArrowDown, ArrowUp, Trash2, AlertTriangle, FileDown, FileText, Image as ImageIcon, Camera, ScanLine, Tag, Layers, CheckSquare, Square } from "lucide-react";
import api from "@/api";
import ScansioneInventario from "@/pages/magazzino/ScansioneInventario";
import ScansioneConsultazione from "@/pages/magazzino/ScansioneConsultazione";
import UbicazioniTab from "@/pages/magazzino/UbicazioniTab";
import StoricoScansioniTab from "@/pages/magazzino/StoricoScansioniTab";
import EtichetteLottoModal from "@/pages/magazzino/EtichetteLottoModal";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORIE = [
  { key: "edile",      label: "Edile",      color: "#1e40af" },
  { key: "elettrico",  label: "Elettrico",  color: "#eab308" },
  { key: "idraulico",  label: "Idraulico",  color: "#0891b2" },
  { key: "generico",   label: "Generico",   color: "#64748b" },
];

import { useAuth } from "@/context/AuthContext";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function Magazzino() {
  const { user } = useAuth();
  const canEdit = user && ["amministratore", "impiegato_amministrativo"].includes(user.role);
  const canSeePrices = user && ["amministratore", "impiegato_amministrativo", "responsabile_cantiere"].includes(user.role);
  const qc = useQueryClient();
  const [tab, setTab] = useState("edile");
  const [view, setView] = useState("materiali");  // materiali | ubicazioni
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [movModal, setMovModal] = useState(null);
  const [photoModal, setPhotoModal] = useState(null); // { src, nome } | null
  const [scanOpen, setScanOpen] = useState(false);    // modalità scansione barcode
  const [consultaOpen, setConsultaOpen] = useState(false); // modalità consultazione scansione
  const [cartLotto, setCartLotto] = useState(() => new Set()); // materiali selezionati per lotto etichette
  const [lottoOpen, setLottoOpen] = useState(false);

  const { data: materiali = [] } = useQuery({
    queryKey: ["magazzino", "materiali", tab],
    queryFn: () => api.get(`/magazzino/materiali?categoria=${tab}`).then((r) => r.data),
  });
  const { data: riepilogo = {} } = useQuery({
    queryKey: ["magazzino", "riepilogo"],
    queryFn: () => api.get("/magazzino/riepilogo").then((r) => r.data),
  });
  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri-attivi"],
    queryFn: () => api.get("/cantieri/attivi").then((r) => r.data),
  });
  const { data: lavoratori = [] } = useQuery({
    queryKey: ["lavoratori"],
    queryFn: () => api.get("/lavoratori").then((r) => r.data),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/magazzino/materiali/${id}`),
    onSuccess: () => { toast.success("Eliminato"); qc.invalidateQueries({ queryKey: ["magazzino"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto" data-testid="page-magazzino">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="overline text-blue-700">Ethos · Inventario</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Magazzino</h1>
          <p className="text-slate-500 text-sm mt-1">Gestione materiali, giacenze, movimenti e costi.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setConsultaOpen(true)}
            data-testid="btn-scan-consultazione"
            title="Scansiona un codice per vedere la scheda articolo"
            className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 text-white text-sm font-semibold px-4 py-2 rounded-sm"
          >
            <ScanLine className="w-4 h-4" /> Scansione barcode
          </button>
          {canEdit && (
            <>
              <a href={`${backendUrl}/api/export/magazzino/excel`}
                 data-testid="magazzino-export-excel"
                 className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50">
                <FileDown className="w-4 h-4" /> Excel
              </a>
              <a href={`${backendUrl}/api/export/magazzino/pdf`}
                 data-testid="magazzino-export-pdf"
                 className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50">
                <FileText className="w-4 h-4" /> PDF
              </a>
              <button onClick={() => { setEditing(null); setOpenForm(true); }} data-testid="btn-new-materiale"
                className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-sm">
                <Plus className="w-4 h-4" /> Nuovo materiale
              </button>
            </>
          )}
        </div>
      </div>

      {/* Riepilogo per categoria */}
      <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        {CATEGORIE.map((cat) => {
          const r = riepilogo[cat.key] || { valore_totale: 0, n_materiali: 0 };
          return (
            <div key={cat.key} className="bg-white border border-slate-200 rounded-md p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{cat.label}</span>
              </div>
              <div className="text-2xl font-black text-slate-900">
                {canSeePrices ? eur(r.valore_totale) : `${r.n_materiali} art.`}
              </div>
              <div className="text-xs text-slate-500">
                {canSeePrices ? `${r.n_materiali} materiali` : `Qta tot: ${r.quantita_totale || 0}`}
              </div>
            </div>
          );
        })}
      </div>

      {/* View switcher: Materiali | Ubicazioni */}
      <div className="flex gap-1 mb-4 border-b border-slate-200">
        <button
          onClick={() => setView("materiali")}
          data-testid="view-materiali"
          className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${
            view === "materiali" ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          📦 Materiali
        </button>
        <button
          onClick={() => setView("ubicazioni")}
          data-testid="view-ubicazioni"
          className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${
            view === "ubicazioni" ? "border-cyan-700 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          📍 Ubicazioni · Etichette QR
        </button>
        <button
          onClick={() => setView("scansioni")}
          data-testid="view-scansioni"
          className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${
            view === "scansioni" ? "border-cyan-700 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          📊 Storico Scansioni
        </button>
      </div>

      {view === "ubicazioni" ? (
        <UbicazioniTab canEdit={canEdit} />
      ) : view === "scansioni" ? (
        <StoricoScansioniTab />
      ) : (
        <>

      {/* Tabs categoria */}
      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {CATEGORIE.map((cat) => (
          <button key={cat.key} onClick={() => setTab(cat.key)} data-testid={`tab-${cat.key}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
              tab === cat.key ? "border-blue-700 text-blue-700" : "border-transparent text-slate-600 hover:text-slate-900"
            }`}>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Tabella materiali */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              {canEdit && (
                <th className="p-3 w-10">
                  <button
                    onClick={() => {
                      if (materiali.every((m) => cartLotto.has(m.materiale_id))) {
                        setCartLotto((prev) => {
                          const next = new Set(prev);
                          materiali.forEach((m) => next.delete(m.materiale_id));
                          return next;
                        });
                      } else {
                        setCartLotto((prev) => {
                          const next = new Set(prev);
                          materiali.forEach((m) => next.add(m.materiale_id));
                          return next;
                        });
                      }
                    }}
                    data-testid="btn-toggle-all-cart"
                    className="p-1 hover:bg-slate-200 rounded-sm"
                    title="Seleziona/deseleziona tutti"
                  >
                    {materiali.length > 0 && materiali.every((m) => cartLotto.has(m.materiale_id)) ? (
                      <CheckSquare className="w-4 h-4 text-cyan-700" />
                    ) : (
                      <Square className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </th>
              )}
              <th className="text-left p-3 w-14">Foto</th>
              <th className="text-left p-3">Materiale</th>
              <th className="text-left p-3">Codice</th>
              <th className="text-right p-3">Quantità</th>
              <th className="text-right p-3">Prezzo unit.</th>
              <th className="text-right p-3">Valore</th>
              <th className="text-left p-3">Fornitore</th>
              <th className="p-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {materiali.length === 0 && (
              <tr><td colSpan={canEdit ? 9 : 8} className="p-8 text-center text-slate-400">
                <Package className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                Nessun materiale in questa categoria. Clicca "Nuovo materiale".
              </td></tr>
            )}
            {materiali.map((m) => {
              const inCart = cartLotto.has(m.materiale_id);
              return (
              <tr key={m.materiale_id} className={`border-t border-slate-100 hover:bg-slate-50 ${inCart ? "bg-cyan-50/50" : ""}`} data-testid={`mat-${m.materiale_id}`}>
                {canEdit && (
                  <td className="p-3">
                    <button
                      onClick={() => setCartLotto((prev) => {
                        const next = new Set(prev);
                        if (next.has(m.materiale_id)) next.delete(m.materiale_id);
                        else next.add(m.materiale_id);
                        return next;
                      })}
                      data-testid={`btn-toggle-cart-${m.materiale_id}`}
                      className="p-1 hover:bg-slate-200 rounded-sm"
                      title={inCart ? "Rimuovi dal lotto" : "Aggiungi al lotto etichette"}
                    >
                      {inCart ? (
                        <CheckSquare className="w-4 h-4 text-cyan-700" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </td>
                )}
                <td className="p-2">
                  {m.foto_base64 ? (
                    <button
                      type="button"
                      onClick={() => setPhotoModal({ src: m.foto_base64, nome: m.nome })}
                      data-testid={`mat-foto-${m.materiale_id}`}
                      title="Visualizza foto"
                      className="block w-10 h-10 rounded-sm overflow-hidden border border-slate-200 hover:ring-2 hover:ring-blue-400"
                    >
                      <img src={m.foto_base64} alt={m.nome} className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <div className="w-10 h-10 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
                      <ImageIcon className="w-4 h-4" />
                    </div>
                  )}
                </td>
                <td className="p-3 font-medium">
                  {m.nome}
                  {m.sotto_scorta && <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-800"><AlertTriangle className="w-3 h-3" />sotto scorta</span>}
                </td>
                <td className="p-3 text-slate-500 text-xs">{m.codice || "—"}</td>
                <td className="p-3 text-right font-mono">{m.quantita_disponibile} {m.unita_misura}</td>
                <td className="p-3 text-right">{canSeePrices ? eur(m.prezzo_unitario) : "—"}</td>
                <td className="p-3 text-right font-semibold">{canSeePrices ? eur(m.valore_totale) : "—"}</td>
                <td className="p-3 text-slate-600">{m.fornitore || "—"}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  {canEdit ? (
                    <>
                      <button onClick={() => setMovModal({ materiale: m, tipo: "carico" })} title="Carica"
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"><ArrowDown className="w-4 h-4" /></button>
                      <button onClick={() => setMovModal({ materiale: m, tipo: "scarico" })} title="Scarica"
                        className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"><ArrowUp className="w-4 h-4" /></button>
                      <button
                        onClick={() => window.open(`${backendUrl}/api/magazzino/materiali/${m.materiale_id}/etichetta.pdf?copies=1&size=medium`, "_blank")}
                        title="Stampa etichetta prodotto (QR + prezzo)"
                        data-testid={`btn-etichetta-${m.materiale_id}`}
                        className="p-1.5 text-cyan-700 hover:bg-cyan-50 rounded"
                      >
                        <Tag className="w-4 h-4" />
                      </button>
                      <button onClick={() => { setEditing(m); setOpenForm(true); }} className="text-xs px-2 py-1 border border-slate-300 hover:bg-slate-50 rounded">Modifica</button>
                      <button onClick={() => window.confirm(`Eliminare "${m.nome}"?`) && del.mutate(m.materiale_id)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 rounded"><Trash2 className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-400 italic">solo lettura</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Barra fissa carrello etichette lotto */}
      {canEdit && cartLotto.size > 0 && (
        <div
          className="sticky bottom-4 mt-4 z-30 bg-cyan-700 text-white p-3 rounded-md shadow-2xl flex items-center gap-3"
          data-testid="cart-bar"
        >
          <Layers className="w-5 h-5" />
          <div className="text-sm font-semibold">
            <b>{cartLotto.size}</b> articol{cartLotto.size === 1 ? "o" : "i"} nel carrello etichette lotto
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setLottoOpen(true)}
            data-testid="btn-open-lotto"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-cyan-700 hover:bg-cyan-50 text-sm font-black rounded-sm"
          >
            🏷️ Stampa etichette lotto ({cartLotto.size})
          </button>
          <button
            onClick={() => setCartLotto(new Set())}
            data-testid="btn-clear-cart"
            className="p-2 hover:bg-cyan-800 rounded-sm"
            title="Svuota carrello"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
        </>
      )}

      {openForm && <MaterialeForm materiale={editing} initialCat={tab}
        onClose={() => setOpenForm(false)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["magazzino"] }); setOpenForm(false); }} />}

      {movModal && <MovimentoModal {...movModal} cantieri={cantieri} lavoratori={lavoratori}
        onClose={() => setMovModal(null)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["magazzino"] }); setMovModal(null); }} />}

      {photoModal && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPhotoModal(null)}
          data-testid="modal-foto-materiale"
        >
          <div className="max-w-3xl max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2 text-white">
              <div className="font-display font-bold text-lg truncate">{photoModal.nome}</div>
              <button onClick={() => setPhotoModal(null)} className="p-1 hover:bg-white/20 rounded" data-testid="btn-close-foto">
                <X className="w-5 h-5" />
              </button>
            </div>
            <img src={photoModal.src} alt={photoModal.nome} className="max-w-full max-h-[80vh] mx-auto border-2 border-white rounded" />
          </div>
        </div>
      )}

      {scanOpen && <ScansioneInventario onClose={() => setScanOpen(false)} cantieri={cantieri} />}
      {consultaOpen && <ScansioneConsultazione onClose={() => setConsultaOpen(false)} canSeePrices={canSeePrices} />}
      {lottoOpen && (
        <EtichetteLottoModal
          items={materiali.filter((m) => cartLotto.has(m.materiale_id))}
          onClose={() => setLottoOpen(false)}
          onCleared={() => setCartLotto(new Set())}
        />
      )}
    </div>
  );
}

function MaterialeForm({ materiale, initialCat, onClose, onSaved }) {
  const [form, setForm] = useState({
    nome: materiale?.nome || "",
    codice: materiale?.codice || "",
    barcode: materiale?.barcode || "",
    categoria: materiale?.categoria || initialCat || "generico",
    unita_misura: materiale?.unita_misura || "pz",
    quantita_disponibile: materiale?.quantita_disponibile ?? 0,
    quantita_minima: materiale?.quantita_minima ?? 0,
    prezzo_unitario: materiale?.prezzo_unitario ?? 0,
    fornitore: materiale?.fornitore || "",
    ubicazione: materiale?.ubicazione || "",
    note: materiale?.note || "",
    foto_base64: materiale?.foto_base64 || null,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine (JPG, PNG, WebP)");
      return;
    }
    if (f.size > 3 * 1024 * 1024) {
      toast.error("Foto troppo grande (max 3MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("foto_base64", reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form,
        quantita_disponibile: Number(form.quantita_disponibile),
        quantita_minima: Number(form.quantita_minima),
        prezzo_unitario: Number(form.prezzo_unitario),
      };
      if (materiale) await api.patch(`/magazzino/materiali/${materiale.materiale_id}`, payload);
      else await api.post("/magazzino/materiali", payload);
      toast.success(materiale ? "Aggiornato" : "Creato");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="font-display text-xl font-bold">{materiale ? "Modifica materiale" : "Nuovo materiale"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          {/* Foto materiale */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Foto materiale (opzionale, max 3MB)</label>
            <div className="flex items-start gap-3">
              <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-sm bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                {form.foto_base64 ? (
                  <img src={form.foto_base64} alt="preview" className="w-full h-full object-cover" data-testid="mat-foto-preview" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-slate-300" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 bg-white text-slate-700 text-xs font-bold uppercase tracking-widest rounded-sm cursor-pointer hover:bg-slate-50">
                  <Camera className="w-3.5 h-3.5" />
                  {form.foto_base64 ? "Cambia foto" : "Scegli foto"}
                  <input type="file" accept="image/*" capture="environment" onChange={onFotoChange} className="hidden" data-testid="mat-foto-input" />
                </label>
                {form.foto_base64 && (
                  <button type="button" onClick={() => set("foto_base64", null)}
                    data-testid="mat-foto-remove"
                    className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs font-bold uppercase tracking-widest rounded-sm">
                    <Trash2 className="w-3.5 h-3.5" /> Rimuovi
                  </button>
                )}
                <div className="text-[11px] text-slate-500">
                  La foto sarà visibile nell'elenco materiali. Da smartphone si può scattare direttamente con la fotocamera.
                </div>
              </div>
            </div>
          </div>
          <input required placeholder="Nome materiale" value={form.nome} onChange={(e) => set("nome", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="mat-nome" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Codice articolo" value={form.codice} onChange={(e) => set("codice", e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <input placeholder="Barcode / QR / EAN (lettore HID)" value={form.barcode} onChange={(e) => set("barcode", e.target.value)}
              data-testid="mat-barcode"
              data-no-uppercase
              autoComplete="off"
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
            <select value={form.categoria} onChange={(e) => set("categoria", e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="mat-categoria">
              {CATEGORIE.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <input placeholder="Unità (pz, kg, m, l...)" value={form.unita_misura} onChange={(e) => set("unita_misura", e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <input placeholder="Fornitore" value={form.fornitore} onChange={(e) => set("fornitore", e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <input placeholder="Ubicazione (corsia/scaffale)" value={form.ubicazione} onChange={(e) => set("ubicazione", e.target.value)}
              data-testid="mat-ubicazione"
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <div><label className="text-xs text-slate-500">Quantità disponibile</label>
              <input type="number" step="0.01" value={form.quantita_disponibile} onChange={(e) => set("quantita_disponibile", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
            <div><label className="text-xs text-slate-500">Quantità minima</label>
              <input type="number" step="0.01" value={form.quantita_minima} onChange={(e) => set("quantita_minima", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
            <div className="col-span-2"><label className="text-xs text-slate-500">Prezzo unitario (€)</label>
              <input type="number" step="0.01" value={form.prezzo_unitario} onChange={(e) => set("prezzo_unitario", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="mat-prezzo" /></div>
          </div>
          <textarea rows={2} placeholder="Note" value={form.note} onChange={(e) => set("note", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">Annulla</button>
            <button type="submit" disabled={saving} data-testid="btn-save-mat"
              className="px-4 py-2 text-sm font-semibold bg-blue-700 hover:bg-blue-800 text-white rounded-sm disabled:opacity-60">
              {saving ? "Salvo..." : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MovimentoModal({ materiale, tipo, cantieri, lavoratori, onClose, onSaved }) {
  const [form, setForm] = useState({ quantita: 1, cantiere_id: "", lavoratore_id: "", note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/magazzino/movimenti", {
        materiale_id: materiale.materiale_id,
        tipo,
        quantita: Number(form.quantita),
        cantiere_id: form.cantiere_id || null,
        lavoratore_id: form.lavoratore_id || null,
        note: form.note,
      });
      toast.success(tipo === "carico" ? "Materiale caricato" : "Materiale scaricato");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const isCarico = tipo === "carico";
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md">
        <div className={`p-4 border-b border-slate-200 flex items-center gap-2 ${isCarico ? "bg-emerald-50" : "bg-orange-50"}`}>
          {isCarico ? <ArrowDown className="w-5 h-5 text-emerald-700" /> : <ArrowUp className="w-5 h-5 text-orange-700" />}
          <h2 className="font-display text-lg font-bold">{isCarico ? "Carica" : "Scarica"} — {materiale.nome}</h2>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div className="text-xs text-slate-500">
            Giacenza attuale: <b>{materiale.quantita_disponibile} {materiale.unita_misura}</b>
          </div>
          <input required type="number" min="0.01" step="0.01" value={form.quantita}
            onChange={(e) => set("quantita", e.target.value)} placeholder={`Quantità in ${materiale.unita_misura}`}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="mov-qty" />
          {!isCarico && (
            <>
              <select value={form.cantiere_id} onChange={(e) => set("cantiere_id", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="mov-cantiere">
                <option value="">— Cantiere (opzionale) —</option>
                {cantieri.map((c) => <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>)}
              </select>
              <select value={form.lavoratore_id} onChange={(e) => set("lavoratore_id", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="">— Lavoratore (opzionale) —</option>
                {lavoratori.map((l) => <option key={l.lavoratore_id} value={l.lavoratore_id}>{l.nome} {l.cognome}</option>)}
              </select>
            </>
          )}
          <textarea rows={2} placeholder="Note (opzionale)" value={form.note} onChange={(e) => set("note", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">Annulla</button>
            <button type="submit" disabled={saving} data-testid="btn-save-mov"
              className={`px-4 py-2 text-sm font-semibold text-white rounded-sm disabled:opacity-60 ${
                isCarico ? "bg-emerald-600 hover:bg-emerald-700" : "bg-orange-600 hover:bg-orange-700"
              }`}>
              {saving ? "Salvo..." : (isCarico ? "Carica" : "Scarica")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
